import React from 'react';

import { Size } from '../../types';
import { FieldCaption } from './field-caption';
import { FieldLabel, FieldMessage, useFieldLayout, useFieldMessageIds } from './field-shell';
import { resolveFieldState } from './field-state';

/** What Field hands its control: spread these onto the element that takes
 * input, or map them to the control's own props (a dropzone's `inputProps`). */
export interface FieldControlProps {
  id: string;
  'aria-labelledby': string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  'aria-required'?: true;
}

interface FieldBaseProps {
  /**
   * Help under the control. Inline content only. Hidden while `error`
   * shows, since the error restates the rule.
   */
  description?: React.ReactNode;
  /** The error message; the control is invalid exactly when it is non-empty. Not shown read-only. */
  error?: string;
  /** Marks the label with an asterisk; the control gets `aria-required`. Not shown read-only. */
  isRequired?: boolean;
  /** The value cannot be changed: no required mark, no error. Outranks the rest. */
  isReadOnly?: boolean;
  /** The control is unavailable; its `description` says why. */
  isDisabled?: boolean;
  /** The label's step, matched to the control's height. */
  size?: Size;
  /**
   * The control, as a function of the ids that wire it to the label and the
   * message line. Content that takes no input (a
   * read-only list of files) is passed as a node instead: the label is then
   * a caption, since a `<label>` must point at a control.
   */
  children: ((control: FieldControlProps) => React.ReactNode) | React.ReactNode;
}

/** Named one way of two: its own visible label, or an element on the page
 * (a question's text) that already names it. */
export type FieldProps = FieldBaseProps &
  (
    | {
        /** The visible name, above the control or beside it (Form decides). */
        label: string;
        'aria-labelledby'?: never;
      }
    | {
        label?: never;
        /** The id of the visible element that names the control (a question's text). */
        'aria-labelledby': string;
      }
  );

/**
 * TRANSITIONAL. The field anatomy (label, control, message) around a control
 * ComplyKit does not own yet: the markdown editor, a file upload, the colour
 * picker and the hand-built response cards. ComplyKit's own controls take
 * `label`, `description` and `error` directly and never sit in a Field.
 *
 * Every use is a park that names its successor, and the import is banned
 * outside the parked files (field-wrapper.md, "Retiring Field"). Field is
 * deleted in the round that lands the last successor.
 */
export const Field = React.forwardRef<HTMLDivElement, FieldProps>(function Field(
  {
    label,
    'aria-labelledby': ariaLabelledBy,
    description,
    error: errorProp,
    isRequired: requiredProp = false,
    isReadOnly: readOnlyProp = false,
    isDisabled: disabledProp = false,
    size = 'md',
    children,
  },
  ref,
) {
  const { error, isRequired, isInvalid } = resolveFieldState({
    error: errorProp,
    isRequired: requiredProp,
    isReadOnly: readOnlyProp,
    isDisabled: disabledProp,
  });
  const { labelPosition, styles } = useFieldLayout(size);
  const { root, label: labelClass, column } = styles;
  const ownLabelId = React.useId();
  const labelId = ariaLabelledBy ?? ownLabelId;
  const controlId = React.useId();
  const { descriptionId, errorId, describedBy } = useFieldMessageIds(description, error);
  const takesControl = typeof children === 'function';

  let name: React.ReactNode = null;
  if (label !== undefined) {
    name = takesControl ? (
      <FieldLabel
        isRequired={isRequired}
        className={labelClass()}
        id={ownLabelId}
        htmlFor={controlId}
      >
        {label}
      </FieldLabel>
    ) : (
      <FieldCaption id={ownLabelId} size={size} isRequired={isRequired}>
        {label}
      </FieldCaption>
    );
  }

  return (
    <div ref={ref} className={root()} data-component="field" data-label-position={labelPosition}>
      {name}
      <div className={column()}>
        {takesControl
          ? children({
              id: controlId,
              'aria-labelledby': labelId,
              'aria-describedby': describedBy,
              'aria-invalid': isInvalid || undefined,
              'aria-required': isRequired || undefined,
            })
          : children}
        <FieldMessage
          description={description}
          error={error}
          descriptionId={descriptionId}
          errorId={errorId}
        />
      </div>
    </div>
  );
});
